import React, { useState } from "react";
import "../../../css/TeleSpecialistModal.css";
import api from "../../../api/axiosConfig";
import TeleRoleAssignmentModal from "./TeleRoleAssignmentModal";
import { showToast } from '../../Shared/Toaster';


const INTERNAL_TOKEN = "super-secret-password"; // move to env in production

interface DocumentFile {
  name: string;
  path: string;
  mimetype: string;
}

interface Props {
  specialist: any;
  onClose: () => void;
}

const TeleSpecialistModal: React.FC<Props> = ({
  specialist,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [showRoleModal, setShowRoleModal] = useState(false);

  const profile = specialist?.metadata?.doctor_profile;

  /* =========================================
        DOCUMENT VIEW (GET /api/s3/view-url)
  ========================================== */

  const openS3Document = async (fullPath: string) => {
    try {
      const parts = fullPath.split("/");
      if (parts.length < 3) throw new Error("Invalid S3 path");

      // first 2 folders → module
      const module = parts.slice(0, 2).join("/");

      // remaining folders → key
      const key = parts.slice(2).join("/");

      const { data } = await api.dataCollectionApi.get(
        "/api/s3/view-url",
        {
          params: { module, key },
          headers: { "x-internal-token": INTERNAL_TOKEN },
        }
      );

      if (!data?.url) throw new Error("No URL returned");

      window.open(data.url, "_blank");
    } catch (error) {
      console.error("S3 preview failed:", error);
      showToast("Failed to open document", 'error');
    }
  };

  const openCertificate = async (key: string) => {
    try {
      if (!key) return;
      const { data } = await api.dataCollectionApi.post('/api/s3/presign-download', {
          key: key,
          expiresIn: 3600
      });
      if (data && data.url) {
          window.open(data.url, '_blank');
      } else {
          throw new Error("No URL returned");
      }
    } catch (error) {
      console.error("Presign failed:", error);
      showToast("Failed to open certificate", 'error');
    }
  };

  /* =========================================
                  APPROVE
  ========================================== */

  const handleApprove = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      await api.auth_api.post(
        "/admin/teledoctor/approve",
        { userId: specialist.auth0Id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // After approve → open role assignment modal
      setShowRoleModal(true);

    } catch {
      setMessage("❌ Approval failed");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================
                CONFIRM REJECT
  ========================================== */

  const confirmReject = async () => {
    if (!rejectReason.trim()) return;

    try {
      setLoading(true);

      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      await api.auth_api.post(
        "/admin/teledoctor/reject",
        {
          userId: specialist.auth0Id,
          reason: rejectReason,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setShowRejectPopup(false);
      setMessage("❌ Rejected successfully");
    } catch {
      setMessage("❌ Reject failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* MAIN MODAL */}
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
          <h2>Tele-Specialist Profile</h2>

          {/* PROFILE IMAGE */}
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <img
              src={specialist.picture}
              alt="doctor"
              style={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                objectFit: "cover",
                border: "3px solid #174350",
              }}
            />
          </div>

          {/* BASIC INFO */}
          <h3>Basic Information</h3>
          <p><b>User ID:</b> {specialist._id}</p>
          <p><b>Email:</b> {specialist.email}</p>
          <p><b>Name:</b> {specialist.name}</p>
          <p><b>Status:</b> {specialist.metadata?.doctor_status}</p>
          <p><b>Role:</b> {specialist.metadata?.role}</p>
          <p><b>Phone Verified:</b> {specialist.metadata?.phone_verified ? "Yes" : "No"}</p>
          <p><b>Profile Complete:</b> {specialist.metadata?.profile_complete ? "Yes" : "No"}</p>

          {/* DOCTOR PROFILE */}
          {profile && (
            <>
              <h3>Doctor Profile Details</h3>

              {Object.entries(profile).map(([key, value]) => {
                if (key === "documents" || key === "additionalDegrees") return null;

                return (
                  <p key={key}>
                    <b>{key}:</b>{" "}
                    {typeof value === "string"
                      ? value
                      : JSON.stringify(value)}
                  </p>
                );
              })}

              {/* ADDITIONAL DEGREES */}
              {profile.additionalDegrees && Array.isArray(profile.additionalDegrees) && profile.additionalDegrees.length > 0 && (
                <>
                  <h4>Additional Degrees</h4>
                  {profile.additionalDegrees.map((deg: any, i: number) => (
                    <div key={i} style={{ marginBottom: 10 }}>
                      {deg.degreeName && <p><b>Degree:</b> {deg.degreeName}</p>}
                      {deg.specialization && <p><b>Specialization:</b> {deg.specialization}</p>}
                      {deg.institutionName && <p><b>Institution:</b> {deg.institutionName}</p>}
                      {deg.certificateKey && (
                        <button
                          className="btn-view-doc"
                          onClick={() => openCertificate(deg.certificateKey)}
                        >
                          View Certificate
                        </button>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* DOCUMENTS */}
              {profile.documents && profile.documents.length > 0 && (
                <>
                  <h4>Documents</h4>
                  {profile.documents.map((doc: DocumentFile, i: number) => (
                    <div key={i} style={{ marginBottom: 10 }}>
                      <p>{doc.name}</p>
                      <button
                        className="btn-view-doc"
                        onClick={() => openS3Document(doc.path)}
                      >
                        View Document
                      </button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {message && (
            <p style={{ marginTop: 15, fontWeight: 600 }}>
              {message}
            </p>
          )}

          {/* ACTION BUTTONS */}
          <div className="modal-actions">
            <button
              className="btn-accept"
              onClick={handleApprove}
              disabled={loading}
            >
              {loading ? "Approving..." : "Approve"}
            </button>

            <button
              className="btn-reject"
              onClick={() => setShowRejectPopup(true)}
              disabled={loading}
            >
              Reject
            </button>

            <button onClick={onClose} className="btn-close">
              Close
            </button>
          </div>
        </div>
      </div>

      {showRoleModal && (
        <TeleRoleAssignmentModal
          userId={specialist._id}
          userEmail={specialist.email}
          userName={specialist.displayName}
          onClose={() => {
            setShowRoleModal(false);
            onClose(); // close main modal after role assignment
          }}
        />
      )}

      {/* SMALL REJECT POPUP */}
      {showRejectPopup && (
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal-content small-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reject Specialist</h3>

            <textarea
              placeholder="Enter reject reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              style={{ width: "100%" }}
            />

            <div className="modal-actions">
              <button
                className="btn-reject"
                onClick={confirmReject}
                disabled={loading}
              >
                Confirm Reject
              </button>

              <button
                className="btn-close"
                onClick={() => setShowRejectPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeleSpecialistModal;